import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  LOCALE_COOKIE,
  normalizeLocale,
  translate,
  translateObject,
  type Dictionary,
  type Locale,
  type TranslateOptions,
} from "@/shared/i18n";
import { loadDictionary } from "@/shared/i18n/dictionary-loader";
import { readLocaleCookie } from "@/shared/i18n/locale-cookie";

export interface TranslateFn {
  (
    key: string,
    opts: { returnObjects: true; defaultValue?: unknown } & Record<string, unknown>,
  ): unknown;
  (key: string, vars?: TranslateOptions): string;
}

interface LocaleContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: TranslateFn;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({
  initialLocale,
  initialDictionary,
  children,
}: {
  initialLocale: Locale;
  initialDictionary: Dictionary;
  children: React.ReactNode;
}) {
  const seededLocale = normalizeLocale(initialLocale);
  const [active, setActive] = useState<{ locale: Locale; dictionary: Dictionary }>(() => ({
    locale: seededLocale,
    dictionary: initialDictionary,
  }));
  const localeRequest = useRef(0);

  const applyLocale = useCallback(async (next: Locale): Promise<void> => {
    const request = ++localeRequest.current;
    try {
      const dictionary = await loadDictionary(next);
      if (request !== localeRequest.current) return;
      setActive({ locale: next, dictionary });
      writeLocaleCookie(next);
    } catch (error) {
      if (request !== localeRequest.current) return;
      console.error(`[simon] could not load the ${next} dictionary:`, error);
    }
  }, []);

  useEffect(() => {
    const requested = readLocaleCookie() ?? normalizeLocale(initialLocale);
    if (requested !== seededLocale) void applyLocale(requested);
  }, [applyLocale, initialLocale, seededLocale]);

  useEffect(() => {
    document.documentElement.lang = active.locale;
  }, [active.locale]);

  const setLocale = useCallback(
    (next: Locale) => {
      void applyLocale(next);
    },
    [applyLocale],
  );

  const value = useMemo<LocaleContextValue>(() => {
    const t = ((key: string, opts?: TranslateOptions) => {
      if (opts && (opts as { returnObjects?: boolean }).returnObjects) {
        return translateObject(
          active.dictionary,
          key,
          (opts as { defaultValue?: unknown }).defaultValue,
        );
      }
      return translate(active.dictionary, key, opts);
    }) as TranslateFn;
    return {
      locale: active.locale,
      setLocale,
      t,
    };
  }, [active, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useTranslation() {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    throw new Error("useTranslation must be used within a LocaleProvider");
  }
  return ctx;
}

function writeLocaleCookie(locale: Locale) {
  // biome-ignore lint/suspicious/noDocumentCookie: this first-party preference must work in browsers without the asynchronous Cookie Store API.
  document.cookie = `${LOCALE_COOKIE}=${locale}; path=/; max-age=31536000; SameSite=Lax`;
}
